import { Button, Icon, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useState } from 'react';
import type { ApplicationDetailedResponse } from '@/hooks/application';
import { useApplicationVersions } from '@/hooks/application_version';
import { useCanWriteById } from '@/hooks/workspace/use_workspace_access';
import { paths } from '@/utility/global_paths';
import { applicationVersionTableColumns } from '../common/table/column_definitions/ApplicationVersion';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { CreateApplicationVersionDialog } from './CreateApplicationVersionDialog';

type Props = {
  application: ApplicationDetailedResponse;
};

export const ApplicationVersionTable = (props: Props) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWriteById(props.application.workspaceId);
  const { data, isLoading, mutate, isValidating } = useApplicationVersions(
    props.application.workspaceId,
    props.application.id
  );

  return (
    <>
      <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="ApplicationVersion">
        <Table
          addButton={
            canWrite
              ? () => (
                  <Tooltip label="Create a new application version" placement="left">
                    <Button
                      borderRightRadius="0px"
                      leftIcon={<Icon as={PlusBold} />}
                      onClick={() => setIsCreateDialogOpen(true)}
                      size="sm"
                      variant="outline"
                    >
                      Create Version
                    </Button>
                  </Tooltip>
                )
              : undefined
          }
          columns={applicationVersionTableColumns}
          count={data?.itemCount || 0}
          data={data?.applicationVersions || []}
          isLoading={Boolean(isLoading || !data)}
          isValidating={isValidating}
          refresh={mutate}
          rowLink={({ id }) =>
            paths.applicationVersionById(props.application.workspaceId, props.application.id, id)
          }
          title="Application Versions"
        />
      </TableStoreProvider>
      <CreateApplicationVersionDialog
        applicationId={props.application.id}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={mutate}
        workspaceId={props.application.workspaceId}
      />
    </>
  );
};
